/**
 * GCIP Authentication Service
 * 
 * Implements IAuthService for Google Cloud Identity Platform authentication.
 * Uses Firebase Auth SDK for sign-in and backend API for account creation.
 * 
 * @module services/auth/GCIPAuthService
 */

import { IAuthService, AuthTokens } from '@/types/auth';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirebaseAuth } from '@/config/firebase';

export class GCIPAuthService implements IAuthService {
  private static readonly TOKEN_KEY = 'gcip_access_token';
  private static readonly USER_ID_KEY = 'gcip_user_id';
  private static readonly REFRESH_TOKEN_KEY = 'gcip_refresh_token';

  /**
   * Login with email and password using Firebase Auth
   * 
   * Uses Firebase SDK to authenticate, matching iOS and Android implementations.
   * 
   * @param email - User's email address
   * @param password - User's password
   * @returns AuthTokens object with access token
   * @throws Error with Firebase error code on failure
   */
  async login(email: string, password: string): Promise<AuthTokens> {
    console.log('[GCIPAuthService] Signing in with Firebase:', email);
    
    try {
      return await this.firebaseSignIn(email, password);
    } catch (error: any) {
      console.error('[GCIPAuthService] Firebase sign-in failed:', error);
      
      // Re-throw with the Firebase error code preserved for UI handling
      const authError = new Error(error.message || 'Sign in failed');
      (authError as any).code = error.code;
      throw authError;
    }
  }

  /**
   * Internal Firebase sign-in helper
   * 
   * Performs the actual Firebase authentication and token storage.
   * Extracted to allow retry after B2C migration.
   */
  private async firebaseSignIn(email: string, password: string): Promise<AuthTokens> {
    const auth = getFirebaseAuth();
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    console.log('[GCIPAuthService] Firebase sign-in successful for:', user.email);
    
    // Get ID token
    const idToken = await user.getIdToken();
    
    if (!idToken) {
      throw new Error('Failed to get ID token from Firebase');
    }
    
    // Store tokens
    localStorage.setItem(GCIPAuthService.TOKEN_KEY, idToken);
    localStorage.setItem(GCIPAuthService.USER_ID_KEY, user.uid);
    // Also store in CommunicationBridge's expected key for compatibility
    localStorage.setItem('access_token', idToken);
    if (user.refreshToken) {
      localStorage.setItem(GCIPAuthService.REFRESH_TOKEN_KEY, user.refreshToken);
    }
    CommunicationBridge.setAccessToken(idToken);
    
    console.log('[GCIPAuthService] Token stored successfully for user:', user.uid);
    
    return { accessToken: idToken };
  }

  /**
   * Login with JWT token (for post-creation auto-login)
   * 
   * Called after successful account creation to establish authenticated session.
   * 
   * @param token - Access token from account creation response
   * @returns AuthTokens object
   * 
   * @example
   * // After account creation
   * const response = await AccountCreationApiService.createAccount(...);
   * await authService.loginWithToken(response.access_token);
   */
  async loginWithToken(token: string): Promise<AuthTokens> {
    console.log('[GCIPAuthService] Login with token');
    
    if (!token) {
      throw new Error('Token is required');
    }

    // Extract user ID from token
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.ref_user_id || payload.sub || payload.user_id || 'unknown';
      
      // Store token in BOTH localStorage AND CommunicationBridge
      localStorage.setItem(GCIPAuthService.TOKEN_KEY, token);
      localStorage.setItem(GCIPAuthService.USER_ID_KEY, userId.toString());
      // Also store in CommunicationBridge's expected key for compatibility
      localStorage.setItem('access_token', token);
      CommunicationBridge.setAccessToken(token);
      
      console.log('[GCIPAuthService] Token stored successfully for user:', userId);
      
      return { accessToken: token };
    } catch (error) {
      console.error('[GCIPAuthService] Failed to parse token:', error);
      throw new Error('Invalid token format');
    }
  }

  /**
   * Logout current user
   * 
   * Signs out from Firebase and clears local token storage.
   */
  async logout(): Promise<void> {
    console.log('[GCIPAuthService] Logging out');
    
    try {
      // Sign out from Firebase
      const auth = getFirebaseAuth();
      await signOut(auth);
      console.log('[GCIPAuthService] Firebase sign-out successful');
    } catch (error) {
      console.warn('[GCIPAuthService] Firebase sign-out error:', error);
      // Continue with local cleanup even if Firebase sign-out fails
    }
    
    // Clear from localStorage
    localStorage.removeItem(GCIPAuthService.TOKEN_KEY);
    localStorage.removeItem(GCIPAuthService.USER_ID_KEY);
    localStorage.removeItem(GCIPAuthService.REFRESH_TOKEN_KEY);
    
    // Clear from TokenClient (legacy keys for backwards compatibility)
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('access_token'); // Also clear the CommunicationBridge key
    
    // Clear CommunicationBridge memory
    CommunicationBridge.setAccessToken('');
    
    console.log('[GCIPAuthService] Logout complete');
  }

  /**
   * Get current access token
   * 
   * Retrieves from CommunicationBridge (memory) or localStorage (persistent).
   * If user is signed into Firebase, refreshes the token.
   * 
   * @returns Access token or null if not authenticated
   */
  async getAccessToken(): Promise<string | null> {
    // First check if Firebase has a current user and get a fresh token
    // This ensures we always get a valid token if the user is authenticated
    try {
      const auth = getFirebaseAuth();
      const user = auth.currentUser;
      if (user) {
        // Get token from Firebase (will auto-refresh if needed)
        const token = await user.getIdToken();
        if (token) {
          localStorage.setItem(GCIPAuthService.TOKEN_KEY, token);
          localStorage.setItem('access_token', token); // Also store in CommunicationBridge's key
          CommunicationBridge.setAccessToken(token);
          return token;
        }
      }
    } catch (error) {
      console.warn('[GCIPAuthService] Failed to get token from Firebase user:', error);
    }
    
    // Fallback: try CommunicationBridge (in-memory)
    let token = CommunicationBridge.getAccessToken();
    
    // If not in memory, try localStorage
    if (!token) {
      token = localStorage.getItem(GCIPAuthService.TOKEN_KEY);
      if (token) {
        // Restore to CommunicationBridge
        CommunicationBridge.setAccessToken(token);
      }
    }
    
    return token;
  }

  /**
   * Check if user is authenticated
   * 
   * Checks CommunicationBridge (memory), localStorage, and Firebase auth state.
   * 
   * @returns true if access token exists or Firebase user is logged in
   */
  isAuthenticated(): boolean {
    const memoryToken = CommunicationBridge.getAccessToken();
    const storedToken = localStorage.getItem(GCIPAuthService.TOKEN_KEY);
    
    // Also check Firebase auth state
    let firebaseUser = false;
    try {
      const auth = getFirebaseAuth();
      firebaseUser = !!auth.currentUser;
    } catch {
      // Firebase not initialized yet, ignore
    }
    
    return !!(memoryToken || storedToken || firebaseUser);
  }

  /**
   * Refresh access token
   * 
   * Uses Firebase SDK to refresh the token if user is signed in.
   * 
   * @returns Fresh access token
   * @throws Error if refresh fails
   */
  async refreshToken(): Promise<string> {
    console.log('[GCIPAuthService] Refreshing token');
    
    try {
      const auth = getFirebaseAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('No authenticated user');
      }
      
      // Force refresh the token
      const newToken = await user.getIdToken(true);
      
      if (!newToken) {
        throw new Error('Failed to refresh token');
      }
      
      // Update stored token
      localStorage.setItem(GCIPAuthService.TOKEN_KEY, newToken);
      localStorage.setItem('access_token', newToken); // Also store in CommunicationBridge's key
      CommunicationBridge.setAccessToken(newToken);
      
      console.log('[GCIPAuthService] Token refreshed successfully');
      return newToken;
    } catch (error: any) {
      console.error('[GCIPAuthService] Token refresh failed:', error);
      throw new Error('Token refresh failed. Please sign in again.');
    }
  }
}
